import BaseGallery from 'ui/BaseGallery';
import mixin from 'privates/utils/mixin';
import HiddenCollapsedMixin from './mixins/HiddenCollapsedMixin';
import tpaUtils from 'privates/utils/tpaUtils';
import {validate} from 'privates/validations/validation';
import constants from 'privates/constants';
import reporter from 'privates/reporter';
import * as members from 'privates/privateMembers';
import imageUtils from 'privates/utils/imageUtils';
import every from 'lodash/every';
import map from 'lodash/map';
import get from 'lodash/get';
import isNil from 'lodash/isNil';

/* REMOVING ALL JSDOC FOR THIS CLASS AND ITS APIs (see https://jira.wixpress.com/browse/WCD-3056) */
export default class Gallery extends BaseGallery { // This is named Gallery on purpose so that the user sees it as Gallery
    constructor(RMI, compId, contextId, wixSelector, controllerId, role, type) {
        super(RMI, compId, contextId, wixSelector, controllerId, role, type);
        mixin(Gallery.prototype, HiddenCollapsedMixin.prototype);
    }

    /* REMOVING JSDOC FROM THIS API (see https://jira.wixpress.com/browse/WCD-3056) */
    get type() {
        return '$w.Gallery';
    }

    /* REMOVING JSDOC FROM THIS API (see https://jira.wixpress.com/browse/WCD-3056) */
    set items(items) {
        const areAllImagesValid = every(items, imageUtils.checkImageValidation);
        if (areAllImagesValid) {
            const newItems = map(items, imageUtils.dataFormat.toModel);
            const RMI = members._RMI.get(this);
            const compId = members._compId.get(this);
            const compName = RMI.getId(compId);
            RMI.setData(compId, {items: newItems});
            tpaUtils.invokeApi(compId, compName, 'setItems', newItems);
        }
    }

    get items() {
        const RMI = members._RMI.get(this);
        const compId = members._compId.get(this);
        const data = RMI.getData(compId);
        if (!data.items) {
            reporter.reportSdkWarning('retrieving items from a Pro Gallery is only supported after the items have been set via code');
            return null;
        }
        const items = data.items || [];
        return map(items, imageUtils.dataFormat.toUser);
    }

    // The following block of JSDoc must be included due to jsdoc-events lint rule:
    /**
     * @function onItemClicked
     * @param {$w.GalleryItemClickedEventHandler} handler
     * @returns {$w.Gallery}
     * @see [clickAction](#clickAction)
     * @memberof $w.Gallery
     * @eventtype itemClicked
     * @eventhandler
     */
    onItemClicked(cb) {
        const compType = members._type.get(this).compType;
        if (validate(compType, 'onItemClicked', {onItemClicked: cb})) {
            const RMI = members._RMI.get(this);
            const compId = members._compId.get(this);
            const compName = RMI.getId(compId);
            tpaUtils.registerToApiEvent(compId, compName, 'onItemClicked', cb);
        }
        return this;
    }

    /* REMOVING JSDOC FROM THIS API (see https://jira.wixpress.com/browse/WCD-3056) */
    get galleryCapabilities() {
        return Object.freeze({
            isPlayable: false,
            hasCurrentItem: false,
            hasNavigationButtons: false
        });
    }

    get clickAction() {
        const RMI = members._RMI.get(this);
        const compId = members._compId.get(this);
        const galleryImageClickAction = get(RMI.getProps(compId), 'galleryImageOnClickAction');
        if (!galleryImageClickAction) {
            reporter.reportSdkWarning('retrieving clickAction from a Pro Gallery is only supported after the items have been set via code');
            return null;
        }
        for (const action in constants.IMAGE_CLICK_ACTIONS) {
            if (constants.IMAGE_CLICK_ACTIONS[action] === galleryImageClickAction) {
                return action;
            }
        }
    }

    set clickAction(action) {
        const compType = members._type.get(this).compType;
        if (validate(compType, 'clickAction', {galleryImageOnClickAction: action})) {
            const galleryImageClickAction = isNil(action) ? constants.IMAGE_CLICK_ACTIONS.none : constants.IMAGE_CLICK_ACTIONS[action];
            if (galleryImageClickAction) {
                const RMI = members._RMI.get(this);
                const compId = members._compId.get(this);
                const compName = this.id;
                RMI.setProps(compId, {galleryImageOnClickAction: galleryImageClickAction});
                tpaUtils.invokeApi(compId, compName, 'setClickAction', galleryImageClickAction);
            }
        }
    }
}



// WEBPACK FOOTER //
// ./js/modules/ui/ProGallery.es6